import React, { useContext, useState } from "react";
import { Formik, Form } from "formik";
import { Spinner } from "reactstrap";
import * as Yup from "yup";
import { LinearProgress, Typography } from "@mui/material";
import { Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ContainerModal, styles } from "../../pages/MyTracks/styles";
import Audio from "../Audio/Audio";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "../LightToolTip/LightToolTip";
import { AudioMetadata, FileState, TrackVersion } from "../../proto/api_pb";
import { postTrack, postTrackVersion } from "../../services/trackService";
import { RpcError } from "grpc-web";
import { S3CredentialsContextInterface } from "../../contexts";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { S3CredentialsContext } from "../../contexts";
import { AddVersionModalProps } from "../../pages/MyTracks/types";
import { CustomLinearProgress } from "../ProgressComponents/CustomLinearProgress";
import { determineSetBpm } from "../../utils/bpmUtil";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import Mixpanel from "../../utils/mixpanel";
import { addVersionModalStyles } from "./styles";
import { APP_BACKGROUND_COLOR, TEXT_COLOR } from "../../theme";

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

const TRACK_VALIDATION_SCHEMA = Yup.object().shape({
  versionLabel: Yup.string().required("Track version must be specified."),
  songUpload: Yup.mixed().required("required"),
  bpm: Yup.number()
    .min(0, "Amount must be greater than 0")
    .max(200, "Amount must be less than 200"),
});

const AddVersionModal: React.FC<AddVersionModalProps> = ({
  open,
  handleClose,
  track,
}) => {
  const { getValidAccessToken } = useGetValidAccessToken();
  const [versionLabel, setVersionLabel] = useState("");
  const [progress, setProgress] = useState(0);
  const [showTrackVersionTooltip, setShowTrackVersionTooltip] = useState(false);
  const [showBpmTooltip, setShowBpmTooltip] = useState(false);
  const [bpmLoading, setBpmLoading] = useState(false);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);

  const newTrack = {
    songUpload: undefined,
    versionLabel: "",
    bpm: 0,
  };

  const handleShowVersionTooltip = () => {
    setShowTrackVersionTooltip((prev) => !prev);
  };

  const handleshowBpmTooltip = () => {
    setShowBpmTooltip((prev) => !prev);
  };

  let clearAudio: any = null;

  const resetFiles = () => {
    clearAudio();
  };

  const saveResetAudioAction = (clearAction: any) => {
    clearAudio = clearAction;
  };

  const handleProgress = (pNum: number) => {
    setProgress(pNum);
  };

  const onSubmit = async (
    values: any,
    {
      resetForm,
      setSubmitting,
    }: {
      resetForm: any;
      setSubmitting: any;
    }
  ) => {
    try {
      setSubmitting(true);
      if (track) {
        const songKey = await awaitableUploadToS3(
          userId,
          values.songUpload,
          `song`,
          values.songUpload!.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          handleProgress
        );
        const songUrl = ASSETS_URL + "/" + songKey;

        // once api calls are done
        const trackVersion = new TrackVersion();
        const audioMetadata = new AudioMetadata();
        audioMetadata.setBpm(values.bpm);

        trackVersion.setLabel(
          versionLabel === "" ? "updated version" : versionLabel
        );
        trackVersion.setAudioMetadata(audioMetadata);
        trackVersion.setTrackUrl(songUrl);
        trackVersion.setTrackId(track.getId()!);
        trackVersion.setFileState(FileState.FILE_STATE_ACTIVE);
        const postTrackVersionResponse = await postTrackVersion(
          trackVersion,
          await getValidAccessToken()
        );
        if (postTrackVersionResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackVersionResponse;
        }
        track.setTrackVersionId(
          postTrackVersionResponse.getTrackVersion()!.getId()
        );
        const postTrackResponse = await postTrack(
          track,
          await getValidAccessToken()
        );
        if (postTrackResponse instanceof RpcError) {
          //TODO: handle api exception gracefully with error modal, etc
          throw postTrackResponse;
        }
        Mixpanel.track("track_version_upload_success");
      }
      setSubmitting(false);
      resetFiles();
      handleClose(true, "New version added!");
    } catch (error) {
      console.log(error);
      alert(error);
      setSubmitting(false);
    }
  };

  return (
    <ContainerModal
      open={open}
      onClose={() => handleClose(false)}
      style={{ overflow: "auto" }}
    >
      <Box
        style={{ backgroundColor: APP_BACKGROUND_COLOR, color: TEXT_COLOR }}
        className="form-container"
      >
        <Box style={styles.modalHeaderContainer}>
          <span style={styles.modalHeader}>Add a New Version of the Track</span>
          <IconButton onClick={() => handleClose(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <hr style={{ marginBottom: 30 }} />
        <Formik
          initialValues={newTrack}
          onSubmit={onSubmit}
          validationSchema={TRACK_VALIDATION_SCHEMA}
        >
          {({
            values,
            handleChange,
            isSubmitting,
            setFieldValue,
            handleBlur,
            errors,
            touched,
          }) => (
            <Form>
              <Box style={{ color: TEXT_COLOR }} className="form-content">
                <label
                  style={{
                    color:
                      errors.songUpload && touched.songUpload
                        ? "red"
                        : TEXT_COLOR,
                  }}
                  htmlFor="audio"
                  className="uploadLabel"
                >
                  Upload Audio
                  <span className="asterisk">*</span>
                </label>
                <Audio
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  transferClearAudio={saveResetAudioAction}
                  determineBpm={determineSetBpm}
                  setBpmLoading={setBpmLoading}
                />
                <p className="input-description">
                  File type supported: MP3, WAV, OGG. Max size: 100 MB
                </p>
                <Box className="input">
                  <label
                    style={{
                      color:
                        errors.versionLabel && touched.versionLabel
                          ? "red"
                          : TEXT_COLOR,
                    }}
                    htmlFor="label"
                  >
                    Version Label<span className="asterisk">*</span>
                    <Box
                      style={{ display: "inline" }}
                      onMouseOver={() => setShowTrackVersionTooltip(true)}
                      onMouseLeave={() => setShowTrackVersionTooltip(false)}
                    >
                      <LightTooltip
                        title="Version label uniquely identifies this uploaded version of your track"
                        placement="top"
                        open={showTrackVersionTooltip}
                        onClick={handleShowVersionTooltip}
                      >
                        <IconButton disableRipple>
                          <HelpOutlineIcon
                            color="info"
                            style={{ height: 16, width: 16 }}
                          />
                        </IconButton>
                      </LightTooltip>
                    </Box>
                  </label>
                  <input
                    style={{
                      backgroundColor: APP_BACKGROUND_COLOR,
                      color: TEXT_COLOR,
                      borderColor:
                        errors.versionLabel && touched.versionLabel
                          ? "red"
                          : TEXT_COLOR,
                    }}
                    type="text"
                    name="label"
                    id="label"
                    placeholder="Add a label for this version of track"
                    value={versionLabel}
                    onChange={(event) => {
                      setFieldValue("versionLabel", event.target.value);
                      setVersionLabel(event.target.value);
                    }}
                    disabled={isSubmitting}
                  />
                </Box>
                <Box className="input">
                  <label htmlFor="bpm" style={styles.modalBpmLabel}>
                    {" "}
                    BPM <span className="optional-input">(optional)</span>
                    <Box
                      style={{ display: "inline" }}
                      onMouseOver={() => setShowBpmTooltip(true)}
                      onMouseLeave={() => setShowBpmTooltip(false)}
                    >
                      <LightTooltip
                        title="Beats per minute (will be detected automatically)"
                        placement="top"
                        open={showBpmTooltip}
                        onClick={handleshowBpmTooltip}
                      >
                        <IconButton disableRipple>
                          <HelpOutlineIcon
                            color="info"
                            style={{ height: 16, width: 16 }}
                          />
                        </IconButton>
                      </LightTooltip>
                    </Box>
                  </label>
                  {!bpmLoading && (
                    <Box style={{ display: "flex", flexDirection: "row" }}>
                      <input
                        style={{
                          backgroundColor: APP_BACKGROUND_COLOR,
                          color: TEXT_COLOR,
                        }}
                        type="number"
                        name="bpm"
                        id="bpm"
                        min="0"
                        max="200"
                        step="1"
                        placeholder="BPM range 0-200"
                        value={values.bpm}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                      />
                    </Box>
                  )}
                  {bpmLoading && (
                    <Box sx={addVersionModalStyles.linearProgressContainer}>
                      <LinearProgress color="inherit" />
                    </Box>
                  )}
                </Box>
                <Button
                  disabled={isSubmitting}
                  fullWidth
                  type="submit"
                  sx={styles.modalSubmitButton}
                  size="small"
                  variant="contained"
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        color="black"
                      />
                      <span style={addVersionModalStyles.addingVersionSpan}>
                        {"Adding Version..."}
                      </span>
                    </>
                  ) : (
                    <Typography variant="h1" sx={styles.modalSubmitButtonText}>
                      {"Add Track Version"}
                    </Typography>
                  )}
                </Button>
                {isSubmitting && <CustomLinearProgress value={progress} />}
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </ContainerModal>
  );
};

export default AddVersionModal;
